/* eslint-disable max-len */
import { AppState } from 'src/app/store'
import { v4 as UUID } from 'uuid'
import {
  Country, MeasurementType, Project,
  ProjectDescription, ProjectImage, SdgCompilance,
  VintageItem
} from 'src/core/models'
import { getSubstringSpaceReplaced } from 'src/core/helpers'

export const projectFormPreviewSelector = (state: AppState): Project => {
  const form = state.projects.projectForm

  // Total Stock
  const getVintageTotalStock = () => {
    if (!form.is_vintage) return form.volume_credits || 0
    return form.vintage_items.map(item => item.stock).reduce((prev, curr) => prev + curr, 0)
  }

  // List of Vintage Items
  const getVintageItems = (): VintageItem[] => {
    const vintages = form.vintage_items
    if (vintages.length === 0) return []
    return vintages.map(vintage => ({
      ...vintage,
      delivery_date: vintage.delivery_date as string,
      available_volume_credits: vintage.stock,
      final_unit_price: null,
      have_available_credits: vintage.stock > 0,
      id: vintage.year,
      stock_balance: vintage.stock,
      token_asa_id: null,
      integration_registry_vintage_id: null
    }))
  }

  // Price
  const getPrice = (): number => {
    if (!form.is_vintage) return form.price || 0
    if (form.vintage_items.length === 0) return 0
    let greaterYear: number = 0
    let price: number = 0
    for (const vintage of form.vintage_items) {
      if (vintage.year > greaterYear) {
        greaterYear = vintage.year
        price = vintage.price
      }
    }
    return price
  }

  // Country
  const getCountry = (): Country => {
    if (!form.country) return null
    const countryList = state.countrySlice.countryList.data
    return countryList.find(c => c.id === +form.country)
  }

  // SDG Compliances
  const getSdgCompliances = (): SdgCompilance[] => {
    const sdgList = state.sdgCompliances.list.data
    return sdgList.filter(sdg => {
      if (form.sdg_compliances.indexOf(sdg.id) !== -1) { return sdg }
    })
  }

  // Project Pictures
  const getProjectPictures = (): ProjectImage[] => {
    return form.pictures.map(picture => ({
      id: null,
      project: null,
      image: picture
    }))
  }

  // Unit of Measurement
  const getUnitOfMeasurement = (): MeasurementType => {
    if (['compensation', 'carbon_forward'].includes(form.kind)) return 'tCO₂'
    if (form.kind === 'contribution') return 'UD'
    if (form.kind === 'energy') return 'MWh'
    if (form.kind === 'biodiversity') return 'CR'
  }

  // Project Description
  const getProjectDescriptions = (): ProjectDescription[] => {
    const locales = ['en', 'es', 'fr', 'ko', 'de', 'pt', 'it', 'pl', 'zh']
    return locales.map(locale => ({
      is_empty: false,
      language: locale,
      text: form[`description_${locale}`]
    }))
  }

  return {
    id: null,
    ...form,
    code: `${getSubstringSpaceReplaced(form.name).toUpperCase()}-${UUID()}`,
    name: form.name || '',
    description: 'Non Empty Text',
    get_vintage_items_sum_stock: getVintageTotalStock(),
    get_vintage_items_available_sum_stock: getVintageTotalStock(),
    vintage_items: getVintageItems(),
    country: getCountry(),
    region: null,
    sdg_compliances: getSdgCompliances(),
    company: null,
    street: null,
    city: null,
    state: null,
    postal_code: null,
    compensated_credits: null,
    available_volume_credits: getVintageTotalStock(),
    co2_balance: getVintageTotalStock(),
    discounts: [],
    price: getPrice(),
    kgco2_minimum_purchase: null,
    token: null,
    original_project: null,
    vat_percentage: null,
    integration_registry: null,
    integration_registry_project_id: null,
    pictures: getProjectPictures(),
    created: null,
    is_upcoming: false,
    is_featured: false,
    bc_address: null,
    recent_purchases: [],
    is_visible_in_climatetrade: true,
    is_property_of_climatetrade: false,
    external_fee: null,
    is_compensation_kind: form.kind === 'compensation',
    is_contribution_kind: form.kind === 'contribution',
    is_energy_type: form.kind === 'energy',
    unit_of_measurement: getUnitOfMeasurement(),
    has_trans_descriptions: true,
    descriptions: getProjectDescriptions(),
    is_free_donation: form.contributionProjectType === 'FREE_DONATION',
    tags: [],
    tax_info: null,
    headline: form.headline_en || form.headline_es || form.headline_fr || form.headline_ko || form.headline_pt || form.headline_de || form.headline_it,
    final_unit_price: null,
    referral: null,
    has_impacts: false,
    impacts: []
  }
}
